import {useEffect, useMemo, useRef, useState} from 'react';
import {useQueryClient} from 'react-query';
import {useNavigate} from 'react-router-dom';
import {Avatar} from '../../../../../../ui/atoms/avatar';
import {ArrowHeadDownIcon, CancelIcon} from '../../../../../../ui/atoms/icons';
import {ConfirmationDialog} from '../../../../../../ui/molecules/confirmation-dialog';
import {activateNotification} from '../../../../../../ui/molecules/notification/activate-notification';
import {parseErrorContent} from '../../../../../../utils/error-handling';
import {useStoreContext} from '../../../../../../store/store-context';
import {groupRequestImpl} from '../admin-group/admin-group-request';
import {groupTypeRequestImpl} from './admin-group-type-request';
import type {TeamsAndGroupsGroup} from './admin-group-type-page';
import {
  getEntityId,
  removeGroupFromResponse,
  shouldDeleteOrphanedGroupType,
} from './group-delete.utils';
import {
  CloseButton,
  DangerButton,
  MemberPrimaryButton,
  Overlay,
  Sidecar,
  SidecarBody,
  SidecarCard,
  SidecarCardSubtitle,
  SidecarCardTitle,
  SidecarFooter,
  SidecarHeader,
  SidecarTitle,
} from '../admin-member/member-redesign.styles';
import {
  DangerCopy,
  DangerZone,
  ErrorCopy,
  Field,
  LeadName,
  LeadSummary,
  SelectMenu,
  SelectOption,
  SelectTrigger,
  SelectWrap,
  TextInput,
} from './group-edit-sidecar.styles';

interface GroupEditSidecarProps {
  group: TeamsAndGroupsGroup;
  groupTypes: any[];
  users: any[];
  confirmDeleteOnOpen?: boolean;
  onClose: () => void;
  onMutated: () => Promise<void> | void;
}

const getName = (user: any) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name || user?.email || 'Unknown member';
};

const normalizeList = (response: any) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getGroupType = (group: TeamsAndGroupsGroup, types: any[]) => {
  if (group.groupType && typeof group.groupType === 'object') {
    return group.groupType;
  }

  return types.find((type) => type.id === group.groupType) || null;
};

const countMembers = (response: any) => {
  if (Array.isArray(response?.members)) return response.members.length;
  if (Array.isArray(response?.results)) return response.results.length;
  if (Array.isArray(response?.data)) return response.data.length;
  if (Array.isArray(response)) {
    const populated = response.find((item) => Array.isArray(item?.members));
    return populated ? populated.members.length : response.length;
  }
  return 0;
};

export const GroupEditSidecar = ({
  group,
  groupTypes,
  users,
  confirmDeleteOnOpen = false,
  onClose,
  onMutated,
}: GroupEditSidecarProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {setGroupTypeInStore} = useStoreContext().groupTypeStore;
  const currentType = getGroupType(group, groupTypes);
  const initialLeadId = getEntityId(group.groupAdmin);
  const [name, setName] = useState(group.name);
  const [leadId, setLeadId] = useState(initialLeadId);
  const [leadOpen, setLeadOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(confirmDeleteOnOpen);
  const [memberCount, setMemberCount] = useState(group.membersCount || 0);
  const [membersLoading, setMembersLoading] = useState(true);
  const [error, setError] = useState('');
  const leadPickerRef = useRef<HTMLDivElement>(null);
  const groupRequest = useMemo(() => new groupRequestImpl(), []);
  const typeRequest = useMemo(() => new groupTypeRequestImpl(), []);

  const selectedLead = users.find((user) => user.id === leadId);
  const trimmedName = name.trim();
  const isDirty = trimmedName !== group.name.trim() || leadId !== initialLeadId;
  const isValid = Boolean(trimmedName);
  const canDelete = !membersLoading && memberCount === 0;

  useEffect(() => {
    let active = true;

    groupRequest
      .fetchGroupMembers(group.id)
      .then((response) => {
        if (active) setMemberCount(countMembers(response));
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setMembersLoading(false);
      });

    return () => {
      active = false;
    };
  }, [group.id, groupRequest]);

  useEffect(() => {
    const closePicker = (event: MouseEvent) => {
      if (!leadPickerRef.current?.contains(event.target as Node)) {
        setLeadOpen(false);
      }
    };

    document.addEventListener('mousedown', closePicker);
    return () => document.removeEventListener('mousedown', closePicker);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (deleteOpen) setDeleteOpen(false);
      else onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [deleteOpen, onClose]);

  const refreshTypes = async (removedTypeId = '') => {
    const response = await typeRequest.fetchGroupType();
    const latestTypes = normalizeList(response).filter(
      (type: any) => getEntityId(type) !== removedTypeId,
    );
    setGroupTypeInStore(latestTypes);
    return latestTypes;
  };

  const cleanupTypeIfOrphaned = async (type: any) => {
    const latestGroups = normalizeList(await groupRequest.fetchGroups());
    if (!shouldDeleteOrphanedGroupType(type, latestGroups, group.id)) return '';

    const typeId = getEntityId(type);
    await typeRequest.deleteGroupType(typeId);
    return typeId;
  };

  const removeDeletedGroupFromCache = () => {
    queryClient.setQueryData(['teams-and-groups-landing'], (response: any) =>
      removeGroupFromResponse(response, group.id),
    );
  };

  const handleSave = async () => {
    if (!isDirty || !isValid || isSaving) return;
    setError('');
    setIsSaving(true);

    try {
      await groupRequest.editGroup(
        {
          name: trimmedName,
          groupAdmin: leadId,
        },
        group.id,
      );

      await refreshTypes();
      await onMutated();
      activateNotification({
        title: 'Success',
        content: 'Group updated successfully',
        kind: 'success',
      });
      onClose();
    } catch (saveError: any) {
      const message = parseErrorContent(saveError);
      setError(message);
      activateNotification({title: 'Error', content: message, kind: 'error'});
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    setError('');
    setIsDeleting(true);

    try {
      const latestMembers = await groupRequest.fetchGroupMembers(group.id);
      if (countMembers(latestMembers) > 0) {
        setMemberCount(countMembers(latestMembers));
        setDeleteOpen(false);
        throw new Error('Only empty groups can be deleted.');
      }

      await groupRequest.deleteGroup(group.id);
      removeDeletedGroupFromCache();

      let deletedTypeId = '';
      let typeCleanupFailed = false;
      try {
        deletedTypeId = await cleanupTypeIfOrphaned(currentType);
      } catch {
        typeCleanupFailed = true;
      }

      if (deletedTypeId) {
        setGroupTypeInStore(
          groupTypes.filter((type: any) => getEntityId(type) !== deletedTypeId),
        );
      }

      try {
        await onMutated();
      } catch {
        // Deletion has already succeeded. A failed background refresh must not
        // turn the completed action into an error or restore the stale record.
      }
      // The list endpoint can briefly return the deleted record. Keep the
      // confirmed deletion authoritative in the local cache after refetching.
      removeDeletedGroupFromCache();
      activateNotification({
        title: typeCleanupFailed ? 'Group deleted' : 'Success',
        content: typeCleanupFailed
          ? 'The group was deleted, but its empty group type could not be removed.'
          : 'Group deleted successfully',
        kind: typeCleanupFailed ? 'warning' : 'success',
      });
      onClose();
      navigate('/workspace-settings/groups');
    } catch (deleteError: any) {
      const message =
        deleteError?.message === 'Only empty groups can be deleted.'
          ? deleteError.message
          : parseErrorContent(deleteError);
      setError(message);
      activateNotification({title: 'Error', content: message, kind: 'error'});
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Overlay onMouseDown={onClose} />
      <Sidecar
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${group.name}`}
        onMouseDown={(event) => event.stopPropagation()}>
        <SidecarHeader>
          <SidecarTitle>Edit Group</SidecarTitle>
          <CloseButton type="button" onClick={onClose}>
            <CancelIcon />
            Close
          </CloseButton>
        </SidecarHeader>

        <SidecarBody>
          <SidecarCard>
            <SidecarCardTitle>Group Details</SidecarCardTitle>
            <SidecarCardSubtitle>
              Update the group name and lead.
            </SidecarCardSubtitle>

            <Field>
              Group Name
              <TextInput
                value={name}
                maxLength={80}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>

            <Field as="div">
              Group lead
              <SelectWrap ref={leadPickerRef}>
                <SelectTrigger
                  type="button"
                  $open={leadOpen}
                  aria-haspopup="listbox"
                  aria-expanded={leadOpen}
                  onClick={() => setLeadOpen((open) => !open)}>
                  {selectedLead ? (
                    <LeadSummary>
                      <Avatar
                        size="sm"
                        tooltip={false}
                        name={getName(selectedLead)}
                        userId={selectedLead.id}
                        src={selectedLead.avatar?.url || ''}
                      />
                      <LeadName>{getName(selectedLead)}</LeadName>
                    </LeadSummary>
                  ) : (
                    <span>No lead assigned</span>
                  )}
                  <ArrowHeadDownIcon />
                </SelectTrigger>
                {leadOpen && (
                  <SelectMenu role="listbox" aria-label="Choose group lead">
                    <SelectOption
                      type="button"
                      role="option"
                      $selected={!leadId}
                      aria-selected={!leadId}
                      onClick={() => {
                        setLeadId('');
                        setLeadOpen(false);
                      }}>
                      No lead assigned
                    </SelectOption>
                    {[...users]
                      .sort((first, second) =>
                        getName(first).localeCompare(getName(second)),
                      )
                      .map((user) => (
                        <SelectOption
                          type="button"
                          role="option"
                          key={user.id}
                          $selected={user.id === leadId}
                          aria-selected={user.id === leadId}
                          onClick={() => {
                            setLeadId(user.id);
                            setLeadOpen(false);
                          }}>
                          <Avatar
                            size="sm"
                            tooltip={false}
                            name={getName(user)}
                            userId={user.id}
                            src={user.avatar?.url || ''}
                          />
                          <LeadName>{getName(user)}</LeadName>
                        </SelectOption>
                      ))}
                  </SelectMenu>
                )}
              </SelectWrap>
            </Field>
          </SidecarCard>

          <SidecarCard>
            <SidecarCardTitle>Danger zone</SidecarCardTitle>
            <SidecarCardSubtitle>
              Destructive group actions cannot be undone.
            </SidecarCardSubtitle>
            <DangerZone>
              <DangerCopy>
                <strong>Delete group</strong>
                <span>
                  {membersLoading
                    ? 'Checking whether this group is empty…'
                    : canDelete
                    ? 'Only empty groups can be deleted.'
                    : `${memberCount} ${
                        memberCount === 1 ? 'member must' : 'members must'
                      } be removed first.`}
                </span>
              </DangerCopy>
              <DangerButton
                type="button"
                disabled={!canDelete}
                onClick={() => setDeleteOpen(true)}>
                Delete group
              </DangerButton>
            </DangerZone>
          </SidecarCard>
          {error && <ErrorCopy role="alert">{error}</ErrorCopy>}
        </SidecarBody>

        <SidecarFooter>
          <MemberPrimaryButton
            type="button"
            disabled={!isDirty || !isValid || isSaving}
            onClick={handleSave}>
            {isSaving ? 'Saving…' : 'Save changes'}
          </MemberPrimaryButton>
        </SidecarFooter>
      </Sidecar>

      <ConfirmationDialog
        open={deleteOpen}
        title="Delete group?"
        confirmLabel="Delete group"
        isLoading={isDeleting}
        confirmDisabled={!canDelete}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDelete}>
        Deleting <strong>{group.name}</strong> cannot be undone. This action is
        only available while the group is empty.
      </ConfirmationDialog>
    </>
  );
};
